import { Stack, type SxProps, type Theme } from '@mui/material';
import classnames from 'classnames';
import { forwardRef, type PropsWithChildren } from 'react';

import {
  PanelFooter,
  type PanelFooterProps,
} from '@/components/panels/panelFooter';
import {
  PanelHeader,
  type PanelHeaderProps,
} from '@/components/panels/panelHeader';

export interface PanelWrapperProps {
  testIdPrefix?: string;
  rounded?: boolean;
  header?: PanelHeaderProps;
  footer?: PanelFooterProps;
  sx?: SxProps<Theme>;
}

export const getTestId = (testIdPrefix: string | undefined, suffix: string) =>
  testIdPrefix ? `${testIdPrefix}-${suffix}` : suffix;

export const PanelWrapper = forwardRef<
  HTMLDivElement,
  PropsWithChildren<PanelWrapperProps>
>((props, ref) => {
  const { testIdPrefix, children, footer, header, rounded } = props;

  return (
    <Stack
      data-testid={getTestId(testIdPrefix, 'root')}
      ref={ref}
      className='h-full flex-1'
      sx={[
        {
          justifyContent: 'space-between',
        },
        ...(Array.isArray(props.sx) ? props.sx : [props.sx ?? false]),
      ]}
    >
      <Stack className='flex-1 min-h-0'>
        {header && <PanelHeader {...header} />}
        <Stack
          className={classnames('overflow-auto h-full', {
            'rounded-b-[16px]': rounded,
          })}
          data-testid={getTestId(testIdPrefix, 'panel-body')}
        >
          {children}
        </Stack>
      </Stack>
      {footer && <PanelFooter {...footer} />}
    </Stack>
  );
});
